// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryEmptyV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4
//
// Empty state for the history page. Two distinct variants:
//   - "no-threads" — user has zero conversations. Big "Start a chat" CTA →
//     navigates to /midas-v2 with a fresh session.
//   - "no-results" — user has threads but the active filter+search returned
//     zero. Caller passes a "Clear" handler to reset.
//   - "error" — fetch failed. Shows error string + retry CTA.
import React from "react";
import { MessageCircle, RotateCw, Search } from "lucide-react";

interface MidasHistoryEmptyV2Props {
  variant: "no-threads" | "no-results" | "error";
  /** Translated copy for the heading. */
  title: string;
  /** Translated copy for the body line. */
  body: string;
  /** Translated label for the primary CTA. */
  ctaLabel?: string;
  /** Click handler for the CTA. */
  onCtaClick?: () => void;
}

const MidasHistoryEmptyV2: React.FC<MidasHistoryEmptyV2Props> = ({
  variant,
  title,
  body,
  ctaLabel,
  onCtaClick,
}) => {
  const Icon = variant === "no-results" ? Search : variant === "error" ? RotateCw : MessageCircle;

  return (
    <div
      role="status"
      className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center"
    >
      <span
        aria-hidden="true"
        className={`flex h-14 w-14 items-center justify-center rounded-full ${
          variant === "error"
            ? "bg-[rgba(212,87,43,0.10)]"
            : "bg-[rgba(44,38,32,0.05)]"
        }`}
      >
        <Icon
          className={`h-6 w-6 ${
            variant === "error" ? "text-midas-terracotta" : "text-midas-warm-graphite"
          }`}
          strokeWidth={1.75}
        />
      </span>
      <div className="flex max-w-[420px] flex-col gap-1">
        <h2
          className="font-sora text-[16px] font-semibold text-midas-graphite"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {title}
        </h2>
        <p
          className="font-sora text-[13px] leading-[20px] text-midas-warm-graphite"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {body}
        </p>
      </div>
      {ctaLabel && onCtaClick && (
        <button
          type="button"
          onClick={onCtaClick}
          className="flex h-11 items-center gap-1.5 rounded-md bg-midas-terracotta px-4 font-sora text-[13px] font-semibold text-white outline-none transition-colors duration-150 hover:bg-[#B84A24] focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
};

export default MidasHistoryEmptyV2;
